import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Loader2, Bell, Mail, Save, RotateCcw, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENTOS = [
  { key: 'post',                   label: 'Nuevo anuncio / post',          hint: 'Se publica algo en Novedades.' },
  { key: 'comentario_post',        label: 'Nuevo comentario en un post',   hint: 'Alguien comenta en una novedad.' },
  { key: 'respuesta_comentario',   label: 'Respuesta a un comentario',     hint: 'Le contestan a alguien (notifica al autor original).' },
  { key: 'tarea',                  label: 'Nueva tarea',                   hint: 'Se publica una tarea / TP / informe.' },
  { key: 'examen',                 label: 'Nuevo examen / quiz',           hint: 'Se publica un examen autocorregido.' },
  { key: 'reunion',                label: 'Nueva reunión',                 hint: 'Se programa una reunión (Meet / Zoom).' },
  { key: 'material',               label: 'Nuevo material',                hint: 'Se sube un archivo o link de estudio.' },
];

export default function CampusNotifConfig({ spaceId }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editable, setEditable] = useState(false);
  const [config, setConfig] = useState({});
  const [defaults, setDefaults] = useState({});
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState(null); // {tipo, mensaje}

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/notif-config`, { headers });
      setConfig(res.data.config || {});
      setDefaults(res.data.defaults || {});
      setEditable(!!res.data.editable);
      setDirty(false);
    } catch (err) {
      setFeedback({ tipo: 'error', mensaje: err.response?.data?.detail || 'Error al cargar la configuración.' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const toggle = (eventoKey, canal) => {
    if (!editable) return;
    setConfig(prev => ({
      ...prev,
      [eventoKey]: {
        alerta: prev[eventoKey]?.alerta ?? true,
        email: prev[eventoKey]?.email ?? false,
        [canal]: !(prev[eventoKey]?.[canal]),
      },
    }));
    setDirty(true);
    setFeedback(null);
  };

  const guardar = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = {};
      EVENTOS.forEach(({ key }) => {
        if (config[key]) {
          payload[key] = {
            alerta: !!config[key].alerta,
            email: !!config[key].email,
          };
        }
      });
      const res = await axios.put(
        `${API_URL}/campus/espacios/${spaceId}/notif-config`,
        payload,
        { headers }
      );
      setConfig(res.data.config || config);
      setDirty(false);
      setFeedback({ tipo: 'ok', mensaje: 'Configuración guardada.' });
    } catch (err) {
      setFeedback({ tipo: 'error', mensaje: err.response?.data?.detail || 'No se pudo guardar.' });
    } finally {
      setSaving(false);
    }
  };

  const restaurar = () => {
    if (!editable) return;
    setConfig({ ...defaults });
    setDirty(true);
    setFeedback(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="notif-config-panel">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Bell className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Notificaciones del espacio</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Elegí, por tipo de evento, si todxs lxs miembrxs reciben una <span className="font-medium">alerta en la campanita</span> y/o un <span className="font-medium">email</span>.
              </p>
              {!editable && (
                <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Sólo coordinadorxs o docentes pueden modificar esta configuración. Estás viendo el modo lectura.</span>
                </div>
              )}
            </div>
          </div>

          {/* Encabezado columnas */}
          <div className="hidden sm:grid grid-cols-12 gap-2 px-3 pb-2 text-xs font-medium text-gray-500 uppercase">
            <div className="col-span-8">Evento</div>
            <div className="col-span-2 text-center">Alerta</div>
            <div className="col-span-2 text-center">Email</div>
          </div>

          <div className="divide-y border rounded-lg">
            {EVENTOS.map(({ key, label, hint }) => {
              const cfg = config[key] || { alerta: true, email: false };
              return (
                <div key={key} className="grid grid-cols-12 gap-2 items-center px-3 py-3" data-testid={`notif-row-${key}`}>
                  <div className="col-span-12 sm:col-span-8">
                    <div className="text-sm font-medium text-gray-900">{label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{hint}</div>
                  </div>
                  <div className="col-span-6 sm:col-span-2 flex sm:justify-center">
                    <ToggleBtn
                      active={!!cfg.alerta}
                      onClick={() => toggle(key, 'alerta')}
                      disabled={!editable}
                      icon={<Bell className="w-3.5 h-3.5" />}
                      labelOn="Activa"
                      labelOff="Off"
                      colorOn="violet"
                      testId={`toggle-${key}-alerta`}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-2 flex sm:justify-center">
                    <ToggleBtn
                      active={!!cfg.email}
                      onClick={() => toggle(key, 'email')}
                      disabled={!editable}
                      icon={<Mail className="w-3.5 h-3.5" />}
                      labelOn="Activo"
                      labelOff="Off"
                      colorOn="emerald"
                      testId={`toggle-${key}-email`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {feedback && (
            <div
              className={`mt-4 text-sm px-3 py-2 rounded-md border ${
                feedback.tipo === 'ok'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}
              data-testid="notif-config-feedback"
            >
              {feedback.mensaje}
            </div>
          )}

          {editable && (
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={restaurar}
                disabled={saving}
                data-testid="notif-config-restaurar"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Restaurar valores por defecto
              </Button>
              <Button
                onClick={guardar}
                disabled={saving || !dirty}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="notif-config-guardar"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Guardar cambios
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-gray-500 px-1">
        Esto se aplica a <span className="font-medium">todxs lxs miembrxs del espacio</span>. Cada miembrx puede silenciar sus propios emails desde la pestaña <span className="font-medium">Miembros</span>.
      </div>
    </div>
  );
}

function ToggleBtn({ active, onClick, disabled, icon, labelOn, labelOff, colorOn, testId }) {
  const baseOn = colorOn === 'emerald'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
    : 'bg-violet-100 text-violet-700 border-violet-300';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors ${
        active ? baseOn : 'bg-gray-50 text-gray-500 border-gray-200'
      } ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:brightness-95 cursor-pointer'}`}
    >
      {icon}
      {active ? labelOn : labelOff}
    </button>
  );
}
